/**
 * Route resolver — maps model types to URL paths.
 *
 * Uses the generated route-map.ts as the single source of truth.
 * Route overrides are applied at scaffold time, so route-map already
 * contains linkPath entries where custom routes exist.
 *
 * @example
 * ```typescript
 * import { buildUrl, getRouteForModel } from '@sourcepilot/ui/framework/data/route-resolver';
 *
 * const url = buildUrl('PDMProduct', product.id);
 * // → "/product/abc123" (uses linkPath) or "/products/abc123" (uses path)
 * ```
 */

let routeMap: Record<string, { path: string; [key: string]: any }> = {};

try {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const rm = require('@/config/route-map');
  routeMap = rm.routeMap || {};
} catch {
  // route-map not yet generated
}

// Known class names + Neo4j-label → class-name reverse map, both built
// once from the generated model-display-config. The route-map keys
// `entry.model` on the class name ("Project"). Backend may stamp either
// the class name or a Neo4j label ("RFLPProject") on parent refs, so
// modelForLabel needs to translate — but ONLY when the input is an
// actual Neo4j label. Passing "Requirement" (a class name) through a
// plain label→class reverse map would wrongly match PDMRequirement.label
// = "Requirement" and return "PDMRequirement". We guard against that by
// checking the class-name set first.
let _classNames: Set<string> | null = null;
let _labelToModel: Record<string, string> | null = null;
function _initDisplayMaps() {
  if (_classNames !== null) return;
  _classNames = new Set();
  _labelToModel = {};
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const cfg = require('@sourcepilot/generated/registries/model-display-config')
      .modelDisplayConfig as Record<string, { label: string }> | undefined;
    if (cfg) {
      for (const [className, meta] of Object.entries(cfg)) {
        _classNames!.add(className);
        if (meta?.label && !(meta.label in _labelToModel!)) {
          _labelToModel![meta.label] = className;
        }
      }
    }
  } catch {
    // model-display-config not generated yet; leave empty.
  }
}

/**
 * Resolve a Neo4j label (e.g. "RFLPProject") to the frontend class name
 * (e.g. "Project") used as `routeMap.<entry>.model`. When the input is
 * already a known class name — or not registered at all — returns it
 * unchanged. This avoids cross-module label collisions (e.g. PDM's
 * `PDMRequirement.label = "Requirement"` clashing with RFLP's
 * `Requirement` class name).
 */
export function modelForLabel(label: string): string {
  _initDisplayMaps();
  if (_classNames!.has(label)) return label;
  return _labelToModel![label] ?? label;
}

export interface RouteInfo {
  path: string;
  slug: string;
  publicRoute?: boolean;
  topLevel?: boolean;
  dagRoot?: boolean;
  module?: string;
}

/**
 * Read the `?default_view=1` URL parameter. When set, `buildUrl` and
 * `getRouteForModel` rewrite every generated link to use the
 * scaffolded `path` instead of the `linkPath` override — so all links
 * from the current page point at the auto-scaffolded default views.
 *
 * Pure URL state, not application state — read fresh on every call.
 * The param is preserved across navigations by `_PRESERVED_PARAMS`,
 * which makes the rewrite transitive: once `?default_view=1` is in
 * the URL, every subsequent navigation keeps it until the user drops
 * it from the URL bar.
 *
 * Client-side only (SSR returns false — initial render falls back to
 * the override; the first client navigation picks the flag back up).
 */
function _defaultViewActive(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("default_view") === "1";
}

/**
 * Get the route configuration for a model type.
 * Uses linkPath (from overrides) if present, otherwise path.
 * Honours `?default_view=1` — see `_defaultViewActive`.
 */
export function getRouteForModel(model: string): RouteInfo {
  // Find the detail route for this model in the route-map
  let detailEntry: any = null;
  for (const [, entry] of Object.entries(routeMap)) {
    const e = entry as any;
    if (e.model === model && e.type === 'detail') {
      detailEntry = e;
      break;
    }
  }

  if (!detailEntry) {
    const segment = model.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
    return { path: `/${segment}`, slug: 'id' };
  }

  // linkPath (from override) takes precedence over path for link generation
  // — UNLESS `?default_view=1` is set on the current URL, in which
  // case ignore the override and use the scaffolded path.
  // Always return a base path WITHOUT [id] — buildUrl appends /${id}
  const rawPath = _defaultViewActive()
    ? detailEntry.path
    : (detailEntry.linkPath ?? detailEntry.path);
  const basePath = rawPath.replace(/\/\[[^\]]*\]$/, '');

  return {
    ...detailEntry,
    path: basePath,
    slug: detailEntry.slug ?? 'id',
  };
}

/**
 * Build a URL for a model instance.
 *
 * Default: standalone detail route (e.g. `/rflprequirements/{id}`).
 *
 * When `parent` is supplied, pick the nested dag-sub-detail route
 * instead (e.g. `/projects/{pid}/requirement/{id}`). The
 * nested variant keeps the navigated-to detail inside the parent's DAG
 * context so `ModelDetailView` reuses the already-subscribed GraphStore
 * and sub-tabs render from the parent's layout. The route-map already
 * carries both variants; `parent` picks which one wins.
 *
 * The parent must be resolved from the ENTITY's own containment chain
 * (e.g. a Requirement's `container*` → Project), not from the page the
 * user happens to be looking at. View code typically derives parent
 * from the URL pathname or by walking the model's static `parentRels`
 * via GraphStore.
 */
/**
 * Build a detail-view URL for `(model, id)` (and an optional parent for
 * nested routes).
 *
 * By default, auto-preserves the chat side-panel URL params (`chat`,
 * `agent`) from `window.location.search` so intra-app navigation keeps
 * the chat panel open. Idempotent — won't double-append if the
 * generated path already has `chat=`.
 *
 * Pass `{ pure: true }` for identity-use (e.g. `tab.pathname` storage
 * compared against path-only `usePathname()`) to opt out of preservation.
 */
export function buildUrl(
  model: string,
  id: string,
  parent?: { model: string; id: string },
  options?: { pure?: boolean }
): string {
  let url: string;
  const childModel = modelForLabel(model);
  // A linkPath override may be a single-placeholder template (the
  // existing pattern, e.g. "/product/[productId]") or a 2-placeholder
  // template that nests under a parent (e.g.
  // "/projects/[projectId]/action/[actionId]"). The latter
  // requires parent context and keeps the resolved URL inside the
  // parent's DAG layout chain — preserving live updates.
  const linkPathRaw = _rawLinkPath(childModel);
  const placeholderCount = linkPathRaw
    ? (linkPathRaw.match(/\[[^\]]+\]/g) ?? []).length
    : 0;

  if (parent && placeholderCount === 2 && linkPathRaw) {
    // First placeholder = parent id, second = entity id. Substitution
    // is positional — match the writer's expectation that the path is
    // written `/{parent-segment}/[parentParam]/{...}/[entityParam]`.
    url = linkPathRaw
      .replace(/\[[^\]]+\]/, parent.id)
      .replace(/\[[^\]]+\]/, id);
  } else if (parent) {
    // Accept either a class name or a Neo4j label on parent.model —
    // backend-stamped parents carry the label ("RFLPProject"); frontend-
    // resolved ones carry the class name ("Project"). Both work.
    const parentModel = modelForLabel(parent.model);
    const nested = _findNestedDetailRoute(childModel, parentModel);
    if (nested) {
      url = nested.replace(/\[[^\]]+\]/, parent.id).replace(/\[[^\]]+\]/, id);
    } else {
      const { path } = getRouteForModel(childModel);
      url = `${path}/${id}`;
    }
  } else {
    const { path } = getRouteForModel(childModel);
    url = `${path}/${id}`;
  }
  if (options?.pure) return url;
  return _preserveSidePanelParams(url);
}

/** Raw `linkPath` string from the route-map for `model`'s detail
 *  route (no fallback to `path`). Returns null when no override, or
 *  when `?default_view=1` is set on the current URL — that param
 *  globally suppresses overrides so callers fall through to the
 *  scaffolded `path`. */
function _rawLinkPath(model: string): string | null {
  if (_defaultViewActive()) return null;
  for (const [, entry] of Object.entries(routeMap)) {
    const e = entry as any;
    if (e.model === model && e.type === "detail" && e.linkPath) {
      return e.linkPath as string;
    }
  }
  return null;
}

/**
 * Append session-scoped URL params from the current URL's query
 * string onto `href`, so intra-app navigation preserves them. Today
 * that's `chat` + `agent` (side-panel chat), plus `view`
 * (`?view=hierarchy|flow` toggle — see `HierarchyFlowToggle`).
 * Client-side only (no-op in SSR).
 *
 * Idempotent per param: if `href` already carries a value for a given
 * param, the existing value wins and we don't overwrite it.
 *
 * Exported so call sites that construct hrefs without going through
 * `buildUrl` (sub-tab generators, hand-built paths) can opt in explicitly.
 */
export function preserveSidePanelParams(href: string): string {
  return _preserveSidePanelParams(href);
}

// URL params propagated automatically by `buildUrl` / `preserveSidePanelParams`.
// Keep narrow: only params whose value is intrinsically session-scoped
// and should survive every intra-app navigation belong here. `mode` is
// the entity page's outer tab selector (e.g. `?mode=architecture`),
// preserved so clicking down the tree opens the child on the same mode.
// Tab-bar clicks build hrefs explicitly (not via this helper) so the
// default tab can clear `mode`.
const _PRESERVED_PARAMS = ["chat", "agent", "view", "default_view", "mode"] as const;

function _preserveSidePanelParams(href: string): string {
  if (typeof window === "undefined") return href;
  const search = new URLSearchParams(window.location.search);
  const incoming: Array<[string, string]> = [];
  for (const key of _PRESERVED_PARAMS) {
    const v = search.get(key);
    if (v !== null && v !== "") incoming.push([key, v]);
  }
  if (incoming.length === 0) return href;
  const hashSplit = href.split("#");
  const base = hashSplit[0];
  const hash = hashSplit.length > 1 ? "#" + hashSplit.slice(1).join("#") : "";
  const qIdx = base.indexOf("?");
  const existing = new URLSearchParams(qIdx >= 0 ? base.slice(qIdx + 1) : "");
  let changed = false;
  for (const [key, value] of incoming) {
    if (!existing.has(key)) {
      existing.set(key, value);
      changed = true;
    }
  }
  if (!changed) return href;
  const basePath = qIdx >= 0 ? base.slice(0, qIdx) : base;
  return `${basePath}?${existing.toString()}${hash}`;
}

/** Find a `dag-sub-detail` route whose source (parent) matches. */
function _findNestedDetailRoute(
  childModel: string,
  parentModel: string
): string | null {
  for (const [, entry] of Object.entries(routeMap)) {
    const e = entry as any;
    if (
      e.type === "dag-sub-detail" &&
      e.model === childModel &&
      e.sourceModel === parentModel
    ) {
      return e.path;
    }
  }
  return null;
}

/**
 * Find the model name for a given URL path segment.
 * E.g. "/products" → "PDMProduct", "/product" → "PDMProduct"
 */
export function modelForPath(pathSegment: string): string | null {
  const normalized = pathSegment.startsWith('/') ? pathSegment : `/${pathSegment}`;

  // Check route-map entries (includes linkPath from overrides)
  for (const [, entry] of Object.entries(routeMap)) {
    const e = entry as any;

    // Check linkPath first (custom routes)
    if (e.linkPath) {
      const linkBase = e.linkPath.replace(/\/\[[^\]]*\]$/, '');
      if (linkBase === normalized && e.model) {
        return e.model;
      }
    }

    // Check generated path
    const basePath = e.path?.replace(/\/\[[^\]]*\]$/, '') || e.path;
    if (basePath === normalized && e.model) {
      return e.model;
    }
  }

  return null;
}

/**
 * Find the registered by-id query name for a model.
 * Looks for a "detail" type entry in the route-map.
 */
export function getDetailQuery(model: string): string | null {
  for (const [, entry] of Object.entries(routeMap)) {
    const e = entry as any;
    if (e.model === model && e.type === 'detail' && e.query) {
      return e.query;
    }
  }
  return null;
}

/**
 * Whether the model has a real flat detail route registered (a
 * `type: "detail"` entry in route-map). Distinct from
 * `getRouteForModel` which falls back to a synthesised path when
 * none exists. Use this to decide whether to render a clickable
 * link to a row's detail page — if false, the row's name is plain
 * text since the synthesised path would 404.
 */
export function hasDetailRoute(model: string): boolean {
  for (const [, entry] of Object.entries(routeMap)) {
    const e = entry as any;
    if (e.type === 'detail' && e.model === model) {
      return true;
    }
  }
  return false;
}

/**
 * Find the model for a relationship name (e.g., "actions" → "PDMAction").
 * Looks at route-map entries with a `relationship` field.
 */
export function modelForRelationship(relationship: string): string | null {
  for (const [, entry] of Object.entries(routeMap)) {
    const e = entry as any;
    if (e.relationship === relationship && e.model) {
      return e.model;
    }
  }
  return null;
}
